import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageSquare, AlertTriangle, CheckCircle, UserPlus, ShieldAlert, X } from 'lucide-react';
import { useNotifications, AppNotification } from '../hooks/useNotifications';

// ---------------------------------------------------------------------------
// Relative time formatter (e.g. "2 min ago", "3 hr ago", "Yesterday")
// ---------------------------------------------------------------------------
function relativeTime(isoString: string): string {
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60)   return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'Yesterday';
  return new Date(isoString).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Icon per notification type
// ---------------------------------------------------------------------------
function NotifIcon({ type }: { type: AppNotification['type'] }) {
  const props = { size: 15, strokeWidth: 1.8 };
  switch (type) {
    case 'new_replies':       return <MessageSquare  {...props} style={{ color: 'var(--sky)' }} />;
    case 'bounced_emails':    return <AlertTriangle  {...props} style={{ color: 'var(--amber, #f59e0b)' }} />;
    case 'campaign_completed':return <CheckCircle    {...props} style={{ color: 'var(--green, #22c55e)' }} />;
    case 'new_leads_added':   return <UserPlus       {...props} style={{ color: 'var(--violet, #8b5cf6)' }} />;
    case 'security_alerts':   return <ShieldAlert    {...props} style={{ color: 'var(--red)' }} />;
    default:                  return <Bell           {...props} style={{ color: 'var(--gray-400)' }} />;
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    markRead,
    markAllRead,
  } = useNotifications();

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Infinite scroll — load next page when near the bottom of the list
  useEffect(() => {
    const list = listRef.current;
    if (!list || !open) return;

    function handleScroll() {
      if (!list) return;
      const { scrollTop, scrollHeight, clientHeight } = list;
      if (scrollHeight - scrollTop - clientHeight < 80 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }

    list.addEventListener('scroll', handleScroll, { passive: true });
    return () => list.removeEventListener('scroll', handleScroll);
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  function handleNotifClick(notif: AppNotification) {
    if (!notif.read_at) {
      markRead(notif.id);
    }
    setOpen(false);
    if (notif.type === 'new_replies') {
      const threadId = notif.data?.thread_id as string | undefined;
      if (threadId) {
        navigate(`/app/replies?thread=${encodeURIComponent(threadId)}&scroll=1`);
      } else {
        navigate('/app/replies');
      }
    }
  }

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="nb-wrap" ref={containerRef}>
      {/* Bell button */}
      <button
        className="tb-icon-btn nb-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="nb-badge" aria-hidden="true">{badgeLabel}</span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="nb-panel">
          {/* Header */}
          <div className="nb-header">
            <span className="nb-header-title">Notifications</span>
            <div className="nb-header-actions">
              {unreadCount > 0 && (
                <button className="nb-mark-all" onClick={() => markAllRead()}>
                  Mark all read
                </button>
              )}
              <button className="nb-close-btn" onClick={() => setOpen(false)} aria-label="Close">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="nb-list" ref={listRef}>
            {isLoading ? (
              <div className="nb-empty">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="nb-empty">
                <Bell size={28} strokeWidth={1.2} style={{ color: 'var(--gray-300)' }} />
                <span>No notifications yet</span>
              </div>
            ) : (
              <>
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`nb-item${notif.read_at ? '' : ' nb-item--unread'}`}
                    onClick={() => handleNotifClick(notif)}
                  >
                    <div className="nb-item-icon">
                      <NotifIcon type={notif.type} />
                    </div>
                    <div className="nb-item-body">
                      <div className="nb-item-title">{notif.title}</div>
                      <div className="nb-item-desc">{notif.body}</div>
                      <div className="nb-item-time">{relativeTime(notif.created_at)}</div>
                    </div>
                    {!notif.read_at && <span className="nb-unread-dot" />}
                  </div>
                ))}
                {isFetchingNextPage && (
                  <div className="nb-loading-more">Loading more…</div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .nb-wrap {
          position: relative;
          flex-shrink: 0;
        }
        .nb-btn {
          position: relative;
        }
        .nb-badge {
          position: absolute;
          top: 4px;
          right: 4px;
          min-width: 16px;
          height: 16px;
          padding: 0 3px;
          border-radius: 8px;
          background: var(--red, #ef4444);
          color: #fff;
          font-size: 9px;
          font-weight: 700;
          line-height: 16px;
          text-align: center;
          border: 1.5px solid #fff;
          pointer-events: none;
        }
        .nb-panel {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          width: 340px;
          background: #fff;
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.12);
          z-index: 200;
          overflow: hidden;
        }
        .nb-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px 10px;
          border-bottom: 1px solid var(--gray-100);
        }
        .nb-header-title {
          font-size: 13px;
          font-weight: 600;
          color: var(--gray-900);
        }
        .nb-header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .nb-mark-all {
          border: none;
          background: none;
          font-size: 11px;
          color: var(--sky);
          cursor: pointer;
          padding: 0;
          font-weight: 500;
        }
        .nb-mark-all:hover {
          text-decoration: underline;
        }
        .nb-close-btn {
          border: none;
          background: none;
          cursor: pointer;
          color: var(--gray-400);
          display: flex;
          align-items: center;
          padding: 2px;
          border-radius: 4px;
        }
        .nb-close-btn:hover {
          background: var(--gray-100);
          color: var(--gray-600);
        }
        .nb-list {
          max-height: 400px;
          overflow-y: auto;
        }
        .nb-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 32px 16px;
          color: var(--gray-400);
          font-size: 13px;
        }
        .nb-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 11px 14px;
          cursor: pointer;
          transition: background 0.12s;
          border-bottom: 1px solid var(--gray-100);
          position: relative;
        }
        .nb-item:last-child {
          border-bottom: none;
        }
        .nb-item:hover {
          background: var(--gray-50);
        }
        .nb-item--unread {
          background: #f0f7ff;
        }
        .nb-item--unread:hover {
          background: #e6f2ff;
        }
        .nb-item-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          background: var(--gray-100);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .nb-item-body {
          flex: 1;
          min-width: 0;
        }
        .nb-item-title {
          font-size: 12.5px;
          font-weight: 600;
          color: var(--gray-900);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .nb-item-desc {
          font-size: 12px;
          color: var(--gray-500);
          margin-top: 2px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .nb-item-time {
          font-size: 11px;
          color: var(--gray-400);
          margin-top: 4px;
        }
        .nb-unread-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--sky);
          flex-shrink: 0;
          margin-top: 5px;
        }
        .nb-loading-more {
          text-align: center;
          padding: 10px;
          font-size: 12px;
          color: var(--gray-400);
        }
        @media (max-width: 480px) {
          .nb-panel {
            width: calc(100vw - 24px);
            right: -8px;
          }
        }
      `}</style>
    </div>
  );
}
